import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import AdminUser from 'AdminUser';
import React, { useCallback, useMemo, useState } from 'react';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import {
  ContentReviewInterface,
  ReviewComment,
  acceptSectionChanges,
} from '../../models/ContentReviewInterface';
import { createContentItemRenderer } from '../../utils/ContentItemRenderer';
import { Palette } from 'Palette';
import { StyledRadio } from '../styles/StyledInputs';
import StyledSection from '../styles/StyledSection';
import ContentDiffComparison from './ContentDiffComparison';

interface ReviewContentComparisonViewProps {
  publishedContent: ContentManagementInterface;
  draftContent: ContentManagementInterface;
  reviewData: ContentReviewInterface;
  currentUser: AdminUser;
  requestedBy: string; // User who requested the review
  requestedOf: string; // User who is assigned to review
  publishingPermission?: boolean; // User has publishing permission
  tone?: keyof typeof Palette;
  defaultComparisonMode?: ComparisonMode;
  onReviewDataUpdate?: (updatedData: ContentReviewInterface) => void;
  onSaveDraft?: (updatedData: ContentReviewInterface) => void;
  onRequestReview?: () => void;
  showActionButtons?: boolean; // Whether to show the action buttons
}

type ComparisonMode = 'published-vs-draft' | 'draft-vs-proposed';

/**
 * HTML diff comparison view for review content
 * Shows inline diff with comments, similar to FullContentDiffViewer but focused on review data
 */
export default function ReviewContentComparisonView({
  publishedContent,
  draftContent,
  reviewData,
  currentUser,
  requestedBy,
  requestedOf,
  publishingPermission = false,
  tone = 'tone1',
  defaultComparisonMode = 'published-vs-draft',
  onReviewDataUpdate,
  onSaveDraft,
  onRequestReview,
  showActionButtons = true,
}: ReviewContentComparisonViewProps) {
  // Check if current user is the one who requested the review
  const isRequestor = currentUser.id === requestedBy;

  // Check if current user is the reviewer (the one assigned to review)
  const isReviewer = currentUser.id === requestedOf;

  // Check if user can accept changes (anyone who is NOT the requestor, or has publishing permission)
  const canAcceptChanges = !isReviewer && (!isRequestor || publishingPermission);

  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(
    isRequestor ? 'draft-vs-proposed' : defaultComparisonMode,
  );
  const [submitting, setSubmitting] = useState(false);

  // Handle accepting changes for a section
  const handleAcceptChanges = useCallback(
    (sectionId: string) => {
      const updatedData = acceptSectionChanges(
        reviewData,
        sectionId,
        currentUser,
        'Changes accepted',
      );
      onReviewDataUpdate?.(updatedData);
    },

    [reviewData, currentUser, onReviewDataUpdate],
  );

  // Handle reverting changes for a section
  const handleRevertToDraft = useCallback(
    async (sectionId: string) => {
      setSubmitting(true);
      try {
        // Remove the change proposal for this section
        const updatedReviewData = {
          ...reviewData,
          changes: reviewData.changes.filter((change) => change.sectionId !== sectionId),
        };

        // Save to backend
        await onReviewDataUpdate?.(updatedReviewData);
      } catch (error) {
        console.error('Error reverting to draft:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [reviewData, onReviewDataUpdate],
  );

  // Prepare section data for actions
  const sectionData = React.useMemo(() => {
    const data: Array<{
      sectionId: string;
      hasChanges: boolean;
      isAccepted: boolean;
    }> = [];

    // Get all unique section IDs from changes and comments
    const allSectionIds = new Set([
      ...reviewData.changes.map((c) => c.sectionId),
      ...reviewData.comments.map((c) => c.sectionId),
    ]);

    allSectionIds.forEach((sectionId) => {
      const change = reviewData.changes.find((c) => c.sectionId === sectionId);
      data.push({
        sectionId,
        hasChanges: change?.changed || false,
        isAccepted: change?.accepted || false,
      });
    });

    return data;
  }, [reviewData]);

  // Render actions for each section (accept/revert buttons)
  const renderActions = useCallback(
    (sectionId: string, hasChanges: boolean, isAccepted: boolean) => {
      if (!hasChanges) return null;

      return (
        <Stack direction="row" spacing={1}>
          {!isAccepted && canAcceptChanges && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => handleAcceptChanges(sectionId)}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              Accept
            </Button>
          )}
          {canAcceptChanges && (
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleRevertToDraft(sectionId)}
              disabled={submitting}
              title="Revert to current draft"
            >
              <UndoIcon />
            </IconButton>
          )}
        </Stack>
      );
    },
    [canAcceptChanges, handleAcceptChanges, handleRevertToDraft, submitting],
  );

  // Handle saving draft with accepted changes
  const handleSaveDraft = useCallback(() => {
    if (onSaveDraft) {
      onSaveDraft(reviewData);
    }
  }, [reviewData, onSaveDraft]);

  // Handle requesting review again
  const handleRequestReview = useCallback(() => {
    if (onRequestReview) {
      onRequestReview();
    }
  }, [onRequestReview]);

  const handleReplyToComment = useCallback(
    async (conversationId: string, message: string) => {
      console.log('handleReplyToComment called with:', {
        conversationId,
        message,
        currentUser,
        onReviewDataUpdate: !!onReviewDataUpdate,
      });
      if (!onReviewDataUpdate) return;

      try {
        // Find the conversation
        const conversation = reviewData.comments.find((c) => c.conversationId === conversationId);
        if (!conversation) return;

        // Add the new message to the conversation
        const updatedConversation = {
          ...conversation,
          comments: [
            ...conversation.comments,
            {
              message,
              submittedBy: { id: currentUser.id, name: currentUser.name },
              createdAt: new Date().toISOString(),
            } as ReviewComment,
          ],
        };

        // Update the review data
        const updatedComments = reviewData.comments.map((c) =>
          c.conversationId === conversationId ? updatedConversation : c,
        );

        const updatedReviewData = {
          ...reviewData,
          comments: updatedComments,
          updatedAt: new Date().toISOString(),
          version: reviewData.version + 1,
        };

        await onReviewDataUpdate(updatedReviewData);
      } catch (error) {
        console.error('Error replying to comment:', error);
      }
    },
    [reviewData, currentUser, onReviewDataUpdate],
  );

  // Get the content to compare based on mode
  const getComparisonContent = useCallback(() => {
    if (comparisonMode === 'draft-vs-proposed') {
      const proposedContent: ContentManagementInterface = {
        ...draftContent,
        content: draftContent.content.map((item, index) => {
          const sectionId = `section-${index}`;
          const change = reviewData.changes.find((c) => c.sectionId === sectionId);
          return change?.changed ? change.contentAfter : item;
        }),
      };
      return {
        original: draftContent,
        new: proposedContent,
      };
    }

    return {
      original: publishedContent,
      new: draftContent,
    };
  }, [comparisonMode, publishedContent, draftContent, reviewData]);

  // Create base renderer for content items
  const baseRenderContentItem = useMemo(
    () => createContentItemRenderer(tone, publishedContent.relatedObjectId || ''),
    [tone, publishedContent.relatedObjectId],
  );

  // Enhanced render function with comment tracking for review mode
  const renderContentItem = useCallback(
    (
      item: any,
      modelId: string,
      isOriginal: boolean,
      compareContent?: ContentManagementInterface,
      isCompareOriginal?: boolean,
    ) => {
      // Get section ID from item index
      const sectionIndex = draftContent.content.findIndex(
        (contentItem) => contentItem.id === item.id,
      );
      const sectionId = `section-${sectionIndex}`;

      // Check if this section has comments
      const sectionComments = reviewData.comments.filter((c) => c.sectionId === sectionId);
      const hasComments = sectionComments.length > 0;
      const commentIds = sectionComments.map((c) => c.conversationId).join(',');

      return (
        <Box
          key={`${item.id}-${isOriginal ? 'original' : 'new'}`}
          sx={{
            mb: 3,
            position: 'relative',
          }}
          data-section-id={sectionId}
          data-has-comments={hasComments}
          data-comment-count={sectionComments.length}
          data-comment-ids={commentIds}
        >
          {baseRenderContentItem(item, modelId, isOriginal)}
        </Box>
      );
    },
    [reviewData.comments, draftContent.content, baseRenderContentItem],
  );

  const comparisonContent = getComparisonContent();

  return (
    <StyledSection tone={tone}>
      {/* Show different UI based on whether user can accept changes */}
      {canAcceptChanges ? (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: Palette[tone].text }}>
            <strong>Review Mode:</strong> You are viewing the proposed changes to the draft content.
            Accept the changes you want to keep, then save and request review again.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ color: Palette[tone].text, mb: 1 }}>
              Compare to:
            </FormLabel>
            <RadioGroup
              row
              value={comparisonMode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setComparisonMode(e.target.value as ComparisonMode)
              }
              sx={{ gap: 2 }}
            >
              <FormControlLabel
                value="published-vs-draft"
                control={<StyledRadio />}
                label="Published vs Draft"
                sx={{ color: Palette[tone].text }}
              />
              <FormControlLabel
                value="draft-vs-proposed"
                control={<StyledRadio />}
                label="Draft vs Proposed Changes"
                sx={{ color: Palette[tone].text }}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      <Box sx={{ position: 'relative' }}>
        <ContentDiffComparison
          originalContent={comparisonContent.original}
          newContent={comparisonContent.new}
          tone={tone}
          renderContentItem={renderContentItem}
          renderActions={renderActions}
          sectionData={sectionData}
          comments={reviewData.comments}
          currentUser={currentUser?.id}
          onReply={handleReplyToComment}
          onAcceptChanges={canAcceptChanges ? handleAcceptChanges : undefined}
        />
      </Box>

      {/* Action Buttons */}
      {showActionButtons && canAcceptChanges && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleSaveDraft} disabled={!onSaveDraft}>
            Save Draft with Accepted Changes
          </Button>
          <Button variant="contained" onClick={handleRequestReview} disabled={!onRequestReview}>
            Request Review Again
          </Button>
        </Box>
      )}
    </StyledSection>
  );
}
